import {
    ChangeDetectionStrategy,
    Component,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import {
    ClientErrorCodes,
    FeatureFlag,
    IProcess,
    ModalResultType,
} from '@models';
import {
    GRID_SERVICE,
    ODataGridComponent,
    STATE_HANDLER,
    urlStateHandler,
} from '@pages/generics/grid';
import { ProcessService } from '@services';
import { encodeParams } from '@utils/encode.utils';

import cloneDeep from 'lodash-es/cloneDeep';
import {
    BehaviorSubject,
    Observable,
} from 'rxjs';
import {
    finalize,
    map,
    switchMap,
} from 'rxjs/operators';

import {
    ProccessSettingsModalComponent,
    ProcessModalComponent,
    ProcessVersionsModalComponent,
} from './modals';

@Component({
    templateUrl: './processes.page.html',
    styleUrls: ['./processes.page.scss'],
    providers: [{
        provide: GRID_SERVICE,
        useClass: ProcessService,
    },
    {
        provide: STATE_HANDLER,
        useValue: urlStateHandler,
    }],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class ProcessesPageComponent
    extends ODataGridComponent<IProcess, number>
    implements OnInit {
    public loading = {
        processDetails$: new BehaviorSubject(false),
    };
    public updatingItems$ = new BehaviorSubject(false);
    public isMonitoringEnabled = this._appState.settings.isFeatureEnabled(FeatureFlag.Monitoring);

    public isCurrentFolderClassic$!: Observable<boolean>;

    private _processService = this._service as ProcessService;

    public get isProcessSettingEnabled() {
        return this._appState.settings.isFeatureEnabled(FeatureFlag.MediaRecording) ||
            this._appState.settings.isFeatureEnabled(FeatureFlag.ProcessSettingAutoStart);
    }

    public get isEveryProcessOutOfDate() {
        return this._grid.selectionManager.selected
            .every((row: IProcess) => !row.isLatestVersion);
    }

    ngOnInit() {
        this._expand$.next(['Environment', 'CurrentVersion']);
        this._select$.next([
            'Id',
            'IsLatestVersion',
            'IsProcessDeleted',
            'ProcessKey',
            'ProcessVersion',
            'Description',
            'Arguments',
            'Name',
        ]);

        this.isCurrentFolderClassic$ = this._appState.user.folder.isClassic$;
    }

    public openModal(model?: IProcess) {
        let clone: IProcess | undefined;
        if (model) {
            clone = cloneDeep(model);
        }

        this._modal
            .openModal(ProcessModalComponent, clone)
            .afterClosedResult()
            .subscribe(res => {
                if (res.type === ModalResultType.Add) {
                    this._refresh$.next();
                }
            });
    }

    public openVersionsModal(model: IProcess) {
        this.loading.processDetails$.next(true);

        this._processService.getInstance(model.id, ['ReleaseVersions'])
            .pipe(
                finalize(
                    () => this.loading.processDetails$.next(false),
                ),
                map((data) => {
                    if (data.arguments && data.arguments.input) {
                        data.arguments.input.forEach(inputArg => inputArg.source = inputArg.hasDefault ? 'package' : null);
                    }

                    return data;
                }),
                switchMap(data =>
                    this._modal
                        .openModal(ProcessVersionsModalComponent, data)
                        .afterClosedResult(),
                ),
            )
            .subscribe(res => {
                if (res.type === ModalResultType.Update) {
                    this._refresh$.next();
                }
            });
    }

    public openProcessSettingsModal(model: IProcess) {
        this.loading.processDetails$.next(true);
        this._processService.getInstance(model.id)
            .pipe(
                finalize(
                    () => this.loading.processDetails$.next(false),
                ),
                switchMap((data) =>
                    this._modal
                        .openModal(ProccessSettingsModalComponent, data)
                        .afterClosedResult(),
                ),
            )
            .subscribe();
    }

    public removeProcesses(items?: Array<IProcess>) {
        const itemsIds = (items || this._grid.selectionManager.selected)
            .map(p => p.id);

        this._modal.onConfirm(null,
            () => {
                this.loading$.next(true);

                itemsIds.forEach((id: any) => {
                    this._processService.delete(id)
                        .subscribe(() => {
                            this._refresh$.next();
                        });
                });
            },
            () => { },
            this._translate.instant('processes_page_remove_message'));
    }

    public onBulkUpdateItems() {
        this.loading$.next(true);
        this._modal.onConfirm(null,
            () => {
                this._processService
                    .updateReleasesToLatestVersionBulk(this._grid.selectionManager.selected.map(s => s.id))
                    .pipe(
                        finalize(() => {
                            this.loading$.next(false);
                            this._refresh$.next();
                        }),
                    )
                    .subscribe((response) => {
                        if (response.FailedItems && response.FailedItems.length) {
                            const message = this._translate.instant('process_page_update_bulk_failed_message', {
                                numberOfProcesses: response.FailedItems.length,
                            });
                            this._snack.error(`${message} (#${ClientErrorCodes.BulkUpdateProcessesFailed})`);
                        }
                    });
            },
            () => this.loading$.next(false),
            this._translate.instant('processes_page_update_bulk_message'),
        );
    }

    public getProcessTriggerParams({ id, name }: IProcess) {
        return {
            process: encodeParams({ id, name }),
        };
    }
}
